import {
  CheckCircle,
  Error,
  ExpandLess,
  ExpandMore,
  InsertDriveFile,
  ModeEdit,
  Report,
  Visibility,
  Warning,
} from "@mui/icons-material";
import { Chip, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import _ from "lodash";
import { useState } from "react";
import { CorePipelineOutput, ValidationResult } from "../../interfaces/doc";
import CoreDisplay from "./CoreDisplay";

interface DocumentDisplayProps {
  title?: string | null;
  coreResult: CorePipelineOutput;
  validation: Record<string, ValidationResult> | null | undefined;
  setPdf: Function;
  toEdit: Function;
}

const DocumentDisplay: React.FC<DocumentDisplayProps> = ({
  title,
  coreResult,
  validation,
  setPdf,
  toEdit,
}) => {
  const [expanded, setExpanded] = useState(false);

  const toggleAccordion = () => {
    setExpanded(!expanded);
  };

  const valid = !!validation
    ? _.entries(validation).some(([_, result]) => result.success === false)
      ? false
      : _.entries(validation).every(([_, result]) => result.success === true)
        ? true
        : undefined
    : null;

  const hasResult = !!coreResult.extraction;

  return (
    <Paper
      square={false}
      elevation={3}
      className="flex flex-col gap-2 p-2 border-[var(--mui-palette-primary-main)] border-solid border-1"
    >
      <div
        className="p-4 flex items-center justify-between cursor-pointer"
        onClick={toggleAccordion}
      >
        <div className="flex justify-between w-full items-center">
          <div className="flex items-center gap-4">
            <InsertDriveFile className="text-gray-400" />
            <div className="flex flex-col">
              {!!title && (
                <Typography className="font-bold">{title}</Typography>
              )}
              <div className="flex">
                {coreResult.classification &&
                  Object.values(coreResult.classification).map(
                    (result, index) => <Chip key={index} label={result} />
                  )}
              </div>
            </div>
          </div>
          <div className="flex items-center">
            {!hasResult && (
              <Tooltip
                slotProps={{
                  tooltip: {
                    className: "bg-[#323639] p-2",
                  },
                }}
                title="Aucune donnée n'a pu être extraite"
                placement="top"
              >
                <Report color="error" className="mr-[8px]" />
              </Tooltip>
            )}
            {valid === false && (
              <Tooltip
                slotProps={{
                  tooltip: {
                    className: "bg-[#323639] p-2",
                  },
                }}
                title="Le document ne respecte pas les règles de validation"
                placement="top"
              >
                <Error color="error" className="mr-[8px]" />
              </Tooltip>
            )}
            {valid === undefined && (
              <Tooltip
                slotProps={{
                  tooltip: {
                    className: "bg-[#323639] p-2",
                  },
                }}
                title="Certaines règles de validation n'ont pas pu être vérifiées"
                placement="top"
              >
                <Warning color="warning" className="mr-[8px]" />
              </Tooltip>
            )}
            {valid === true && (
              <Tooltip
                slotProps={{
                  tooltip: {
                    className: "bg-[#323639] p-2",
                  },
                }}
                title="Le document respecte les règles de validation"
                placement="top"
              >
                <CheckCircle color="success" className="mr-[8px]" />
              </Tooltip>
            )}
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                toEdit();
              }}
            >
              <ModeEdit />
            </IconButton>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                setPdf();
              }}
            >
              <Visibility />
            </IconButton>
            {hasResult && (
              <IconButton>
                {expanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            )}
          </div>
        </div>
      </div>
      {hasResult && expanded && (
        <CoreDisplay coreResult={coreResult} validation={validation} />
      )}
    </Paper>
  );
};

export default DocumentDisplay;
